import { useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/cn';

interface NavItem {
  label: string;
  path: string;
  roles?: Array<'admin' | 'quartermaster' | 'user'>;
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Home', path: '/' },
  { label: 'Gear', path: '/gear' },
  { label: 'People', path: '/people', roles: ['admin', 'quartermaster'] },
  { label: 'Import', path: '/import', roles: ['admin', 'quartermaster'] },
  { label: 'Inspections', path: '/inspections' },
  { label: 'Settings', path: '/settings', roles: ['admin', 'quartermaster'] },
  { label: 'Activity', path: '/activity', roles: ['admin', 'quartermaster'] },
  { label: 'Account', path: '/account' },
];

interface MobileNavProps {
  open: boolean;
  onClose: () => void;
}

export function MobileNav({ open, onClose }: MobileNavProps) {
  const { user } = useAuth();
  const location = useLocation();
  const overlayRef = useRef<HTMLDivElement>(null);
  const role = user?.role ?? 'user';

  const visibleItems = NAV_ITEMS.filter((item) => !item.roles || item.roles.includes(role));

  // Close on route change
  useEffect(() => {
    if (open) onClose();
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on Escape
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && open) onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      {/* Overlay */}
      <div
        ref={overlayRef}
        className="fixed inset-0 z-40 bg-black/50 md:hidden"
        onClick={onClose}
        aria-hidden="true"
      />
      {/* Drawer */}
      <nav
        className="fixed inset-y-0 left-0 z-50 w-64 bg-bg-secondary shadow-lg md:hidden"
        aria-label="Mobile navigation"
      >
        <div className="flex h-14 items-center justify-between border-b border-border-default px-4">
          <span className="text-sm font-bold text-text-primary">PPE Tracker</span>
          <button
            onClick={onClose}
            className="rounded-md p-1.5 text-text-muted hover:bg-bg-hover hover:text-text-primary"
            aria-label="Close menu"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="space-y-1 p-2">
          {visibleItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              className={({ isActive }) =>
                cn(
                  'block rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-accent-primary/10 text-accent-primary'
                    : 'text-text-secondary hover:bg-bg-hover hover:text-text-primary',
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </div>
      </nav>
    </>
  );
}
